import { formatDateIT } from './format.js';

const DAY_TRANSITION_MS = 1500;

let transitionTimer = null;

function isScrollAtBottom(el, threshold = 4) {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
}

function isScrollAtTop(el, threshold = 4) {
  return el.scrollTop <= threshold;
}

function dayCards(scroller) {
  return [...scroller.querySelectorAll('.card-day--snap')];
}

function scrollToDayCard(card) {
  card.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function showDayTransition(transitionEl, label, { duration = DAY_TRANSITION_MS } = {}) {
  if (!transitionEl) return;
  const timeEl = transitionEl.querySelector('time');
  if (timeEl) timeEl.textContent = label;

  transitionEl.hidden = false;
  transitionEl.classList.remove('days-date-transition--out');
  void transitionEl.offsetWidth;
  transitionEl.classList.add('days-date-transition--in');

  clearTimeout(transitionTimer);
  transitionTimer = setTimeout(() => {
    transitionEl.classList.remove('days-date-transition--in');
    transitionEl.classList.add('days-date-transition--out');
    setTimeout(() => {
      if (!transitionEl.classList.contains('days-date-transition--out')) return;
      transitionEl.hidden = true;
      transitionEl.classList.remove('days-date-transition--out');
    }, 380);
  }, duration);
}

function bindBodyHandoff(body, scroller) {
  const onWheel = (e) => {
    const card = body.closest('.card-day--snap');
    if (!card) return;

    const cards = dayCards(scroller);
    const index = cards.indexOf(card);
    if (index < 0) return;

    if (e.deltaY > 0 && isScrollAtBottom(body)) {
      const next = cards[index + 1];
      if (next) {
        e.preventDefault();
        scrollToDayCard(next);
      }
      return;
    }

    if (e.deltaY < 0 && isScrollAtTop(body)) {
      const prev = cards[index - 1];
      if (prev) {
        e.preventDefault();
        scrollToDayCard(prev);
      }
    }
  };

  body.addEventListener('wheel', onWheel, { passive: false });

  let touchStartY = 0;
  body.addEventListener(
    'touchstart',
    (e) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    },
    { passive: true },
  );

  body.addEventListener(
    'touchend',
    (e) => {
      const touchEndY = e.changedTouches[0]?.clientY ?? touchStartY;
      const delta = touchStartY - touchEndY;
      if (Math.abs(delta) < 48) return;

      const card = body.closest('.card-day--snap');
      if (!card) return;

      const cards = dayCards(scroller);
      const index = cards.indexOf(card);
      if (index < 0) return;

      if (delta > 0 && isScrollAtBottom(body)) {
        const next = cards[index + 1];
        if (next) scrollToDayCard(next);
      } else if (delta < 0 && isScrollAtTop(body)) {
        const prev = cards[index - 1];
        if (prev) scrollToDayCard(prev);
      }
    },
    { passive: true },
  );
}

/**
 * Wheel/touch at inner edges hands off to the day snap scroller (one day at a time).
 */
export function bindDayScrollHandoff(scroller) {
  if (!scroller) return;
  scroller.querySelectorAll('.card-day__body').forEach((body) => bindBodyHandoff(body, scroller));
}

/**
 * Full-screen day transition when the active day changes.
 */
export function bindDayTransition(scroller, transitionEl, { initialDayKey = null } = {}) {
  if (!scroller || !transitionEl || !('IntersectionObserver' in window)) return;

  const cards = scroller.querySelectorAll('.card-day--snap');
  if (!cards.length) return;

  let activeKey = initialDayKey;
  let primed = Boolean(initialDayKey);

  if (initialDayKey) {
    showDayTransition(transitionEl, formatDateIT(initialDayKey));
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting && e.intersectionRatio >= 0.55)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;

      const dayKey = visible.target.dataset.dayKey;
      if (!dayKey || dayKey === activeKey) return;

      if (!primed) {
        activeKey = dayKey;
        primed = true;
        return;
      }

      activeKey = dayKey;
      showDayTransition(transitionEl, formatDateIT(dayKey));
    },
    { root: scroller, threshold: [0.55, 0.7, 0.85] },
  );

  cards.forEach((card) => observer.observe(card));
}
